/**
 * Icon asset path and tooltip label for each status group, keyed once to
 * avoid duplicating the same string literal across multiple status codes
 * (each raw `statusCode` maps to one of these groups below).
 */
const STATUS_GROUP_ICON: Record<string, string> = {
  HOLD: '/assets/images/Hold.svg',
  PARTIALLY_APPROVED: '/assets/images/PartiallyApproved.svg',
  PARTIALLY_DENIED: '/assets/images/PartiallyDenied.svg',
  OVERRIDDEN: '/assets/images/Overridden.svg',
}

/**
 * Status to icon mapping for request status display, reusing the same
 * asset paths as {@link STATUS_GROUP_ICON} so each icon path is defined once.
 */
const STATUS_ICON_MAP: Record<string, string> = {
  'Denied Overridden To Approve': STATUS_GROUP_ICON.OVERRIDDEN,
  'Overridden': STATUS_GROUP_ICON.OVERRIDDEN,
  'Partially Approved': STATUS_GROUP_ICON.PARTIALLY_APPROVED,
  'Partial Retraction': STATUS_GROUP_ICON.PARTIALLY_APPROVED,
  'Partially Denied': STATUS_GROUP_ICON.PARTIALLY_DENIED,
  'On Hold': STATUS_GROUP_ICON.HOLD,
}

const STATUS_GROUP_LABEL: Record<string, string> = {
  HOLD: 'On Hold',
  PARTIALLY_APPROVED: 'Partially Approved',
  PARTIALLY_DENIED: 'Partially Denied',
  OVERRIDDEN: 'Overridden',
}

/**
 * Maps raw API `statusCode` values to a status group above.
 * Using `statusCode` is preferred over `status`/`detail` strings because
 * statusCode is a stable contract from the backend and never localised.
 *
 * The OVERRIDDEN group mirrors the override-denied status codes used in
 * buttonStateUtils.ts (isRetractionDeniedStatus) and RequestDetailsView.tsx
 * (isOverrideDeniedRequest) so the icon stays consistent with the rest of
 * the override-handling logic.
 */
const STATUSCODE_GROUP_MAP: Record<string, string> = {
  // On Hold variants
  SUBMITTED_ONHOLD: 'HOLD',
  RTSUBMITTED_ONHOLD: 'HOLD',
  RESUBMITTED_ONHOLD: 'HOLD',
  PRTSUBMITTED_ONHOLD: 'HOLD',
  // Partially Approved
  PAPPROVED: 'PARTIALLY_APPROVED',
  // Partially Denied
  PDENIED: 'PARTIALLY_DENIED',
  // Denied overridden to Approved — only the exact OVERRIDDEN code shows the icon
  OVERRIDDEN: 'OVERRIDDEN',
}

/**
 * Returns the icon path for a given raw API `statusCode`.
 * Prefer this over {@link getStatusIcon} in the requestor flow because
 * statusCode is a stable backend constant, independent of display strings.
 *
 * @param statusCode - The raw API statusCode (e.g. "PAPPROVED", "SUBMITTED_ONHOLD").
 * @returns The SVG asset path, or `null` if the statusCode has no icon mapping.
 */
export const getStatusIconByCode = (statusCode: string): string | null => {
  const group = STATUSCODE_GROUP_MAP[statusCode]
  return group ? STATUS_GROUP_ICON[group] : null
}

/**
 * Returns the human-readable tooltip label for a given raw API `statusCode`.
 *
 * @param statusCode - The raw API statusCode (e.g. "PAPPROVED", "SUBMITTED_ONHOLD").
 * @returns The display label, or the original statusCode if no mapping exists.
 */
export const getStatusLabelByCode = (statusCode: string): string => {
  const group = STATUSCODE_GROUP_MAP[statusCode]
  return group ? STATUS_GROUP_LABEL[group] : statusCode
}

/**
 * Returns the icon path for a given request status display string.
 * Used by the approver flow which does not always have access to statusCode.
 *
 * @param status - The primary status display string (e.g. "On Hold", "Partially Denied").
 * @param detail - Optional detail string checked only when status is "Approved"
 *   or "Denied" (e.g. "Partially Approved" / "Partially Denied").
 * @returns The SVG asset path, or `null` if no mapping exists.
 */
export const getStatusIcon = (status: string, detail?: string): string | null => {
  if ((status === 'Approved' || status === 'Denied') && detail) {
    return STATUS_ICON_MAP[detail] ?? null
  }
  return STATUS_ICON_MAP[status] ?? null
}
